import { get } from "lodash";
import { useContext, useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Alert, Box, Typography, Button, Link } from "@mui/material";
import GridPage from "components/layout/GridPage";
import { useListTasksForAdvisor } from "services/tasks";
import { SearchContext } from "components/context/SearchContextProvider";
import TasksTable from "components/tasks/TasksTable";
import { TaskFilter } from "components/tasks/FilterContext";
import useIsMobile from "components/hooks/useIsMobile";
import {
  AddFilterContext,
  FilterContext,
} from "components/tasks/FilterContext";
import useSessionStorageState from "components/hooks/useSessionStorageState";
import SearchBar from "components/common/SearchBar";
import useTitle from "components/hooks/useTitle";
import { ReactComponent as IdeaIcon } from "../icons/Menu/Idea.svg";
import { buildRequest } from "components/tasks/utils";

const _Wrapped = () => {
  const navigate = useNavigate();
  const { setTasksOnly } = useContext(SearchContext);
  const { getFilters } = useContext(FilterContext);
  const isMobile = useIsMobile();
  const [query, setQuery] = useState("");
  const { loading, data, request } = useListTasksForAdvisor(() => {
    setTasksOnly(true);
  });

  const fetchTasks = (filters: TaskFilter[]) => {
    request(buildRequest(filters.filter((f) => f.field !== "status")));
  };

  const onAddTaskClick = () => {
    navigate("/tasks/add", { state: { families: data?.families } });
  };

  useEffect(() => {
    fetchTasks(getFilters());
  }, []);

  let resultingTasks = data?.tasks || [];
  if (query) {
    const queryPaths = ["title", "priority", "state", "family.name"];
    const regexPattern = new RegExp(
      query
        .split(" ")
        .map((sub) => `(${sub})`)
        .join("|"),
      "i",
    );

    resultingTasks = resultingTasks.filter((t) => {
      const searchableValues = queryPaths.map((path) => get(t, path)).join(" ");
      return regexPattern.test(searchableValues);
    });
  }
  return (
    <GridPage
      sx={{
        maxWidth: "1000px",
        margin: isMobile ? "" : "auto",
        padding: isMobile ? "32px 24px" : "48px",
        gap: "20px",
      }}
    >
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <Typography variant="display" id="tableTitle" component="div">
          Tasks
        </Typography>
        {isMobile && (
          <Button size="small" onClick={onAddTaskClick}>
            Add task
          </Button>
        )}
      </Box>
      <Alert
        icon={
          <IdeaIcon
            fill="#198282"
            strokeWidth="0.5"
            style={{ marginRight: "-4px" }}
          ></IdeaIcon>
        }
        sx={{
          borderRadius: "12px",
          background: "#f6f9f6",
          fontWeight: "500",
          color: "#198282",
          alignSelf: "stretch",
          padding: "12px 20px",
        }}
        action={
          <Button sx={{ height: "28px" }} variant="text" size="small">
            <Link href="/task-discovery">View</Link>
          </Button>
        }
      >
        See the tasks we're suggesting to clients
      </Alert>
      <Box
        sx={{
          display: "flex",
          flexDirection: isMobile ? "column-reverse" : "row",
          justifyContent: "space-between",
          gap: "8px",
          alignItems: "center",
          width: "100%",
        }}
      >
        <Box sx={{ width: "100%" }}>
          <SearchBar
            fullWidth={isMobile}
            onQuery={setQuery}
            onKeyPress={true}
            sx={{ marginLeft: "0px" }}
          />
        </Box>
        {!isMobile && (
          <Button
            size="small"
            onClick={onAddTaskClick}
            sx={{ minWidth: "120px" }}
          >
            Add task
          </Button>
        )}
      </Box>

      <Box
        sx={{
          width: "100%",
          background: "var(--base-white, #FFF)",
          display: "flex",
          flexDirection: "column",
          padding: "0px",
        }}
      >
        <TasksTable
          loading={loading}
          fetchTasks={fetchTasks}
          tasks={resultingTasks}
          listName="All tasks"
          listPath="/tasks"
          families={
            data?.families.map((f) => ({ label: f.name, value: f.ref })) || []
          }
        />
      </Box>
    </GridPage>
  );
};

export default () => {
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const familyParam = queryParams.get("familyRefs");
  const familyRefs: string[] | undefined = familyParam
    ? familyParam.split(",")
    : undefined;

  const [storedPriorities, setStoredPriorities] = useSessionStorageState<
    string[]
  >("faye.tasks.filters-priorities", []);
  const [storedStages, setStoredStages] = useSessionStorageState<string[]>(
    "faye.tasks.filters-stages",
    [],
  );
  const [storedFamilies, setStoredFamilies] = useSessionStorageState<string[]>(
    "faye.tasks.filters-families",
    [],
  );
  const [selectedTimeRange, setSelectedTimeRange] =
    useSessionStorageState<string>("faye.tasks.filters-time-range", "");

  const onFilterChange = (filters: TaskFilter[]) => {
    setStoredPriorities(
      filters.find((f) => f.field === "priority")?.value || [],
    );
    setStoredStages(filters.find((f) => f.field === "stage")?.value || []);
    setStoredFamilies(filters.find((f) => f.field === "family")?.value || []);
    setSelectedTimeRange(
      filters.find((f) => f.field === "timeRange")?.value || "",
    );
  };

  useTitle("Tasks");
  return (
    <AddFilterContext
      initialStages={storedStages || []}
      initialFamilies={familyRefs || storedFamilies}
      initialPriorities={storedPriorities}
      initialTimeRange={selectedTimeRange}
      onFilterChange={onFilterChange}
    >
      <_Wrapped />
    </AddFilterContext>
  );
};
